"use client";

import { HexColorInput, HexColorPicker } from "react-colorful";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * A labelled color swatch that opens a hex color picker. Works in `#rrggbb`
 * (the caller normalizes rgba ↔ hex + handles any alpha separately). `onCommit`
 * fires when the popover closes — the caller uses it to close the undo group so
 * a drag through the picker is one undo step.
 */
export function ColorField({
  label,
  value,
  onChange,
  onCommit,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (hex: string) => void;
  onCommit?: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-foreground/70 text-sm">{label}</span>
      <Popover
        onOpenChange={(open) => {
          if (!open) onCommit?.();
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label={label}
            className="h-6 w-10 rounded border border-foreground/20 disabled:opacity-50"
            style={{ background: value }}
          />
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto space-y-2 p-3">
          <HexColorPicker color={value} onChange={onChange} />
          <div className="flex items-center gap-1">
            <span className="text-foreground/50 text-sm">#</span>
            <HexColorInput
              color={value}
              onChange={onChange}
              prefixed={false}
              className="w-24 rounded border bg-background px-2 py-1 font-mono text-sm uppercase outline-hidden"
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
